/**
 * useAnnouncements (B10) — speaks the §H.3 moments through bus "announce".
 * <Announcer/> calls it; call it yourself only with <Announcer auto={false}/>.
 * A second concurrent caller is a no-op. `skip` switches off moments that a
 * module already speaks (at the time of writing B7: group, search; B8: select;
 * B9: mood). Without it the Announcer's dedupe keeps one of each pair.
 *
 * | Moment             | Source                                   | Text (announceText.ts)                           |
 * |--------------------|------------------------------------------|--------------------------------------------------|
 * | Group change       | store.group (not caused by a selection    | "Social infrastructure. 44 places."              |
 * |                    | or a chip), 250 ms debounce               |                                                  |
 * | Chip / bloom done  | bus bloom:done                           | "Education. 23 schools and colleges, nearest 9 minutes off-peak." |
 * | Search             | store.query, 600 ms debounce             | "12 places match." · "No places match."          |
 * | Select, at arrival | bus route:drawn for the selection; or    | "Pune International Airport. 13 minutes off-peak,|
 * |                    | 900 ms after selecting when no route     |  9.8 kilometres by road, north-west of the site."|
 * |                    | draw starts (reduced motion, no route)   |                                                  |
 * | Deselect           | store.selectedId → null                  | "Details closed."                                |
 * | Mood               | store.mood, in explore only              | "Map light: Golden hour."                        |
 * | Layer toggled      | store.layers (one flag changed)          | "Contours shown." · "Distance rings hidden."     |
 * | Drive start / end  | phase → drive · bus drive:end            | "Taking the drive to X. Press Escape to stop." · "Arrived at X." · "Drive stopped." |
 * | Load error         | store.loadError (assertive)              | "The map couldn't load. Check the connection, then try again." |
 * | Intro done         | bus intro:done                           | "Map ready."                                     |
 *
 * Share ("Link copied.") is announced by the module that copies. Nothing is spoken
 * during kiosk attract (the Announcer drops it). Duplicates from other modules
 * (e.g. the card speaking the same selection) are dropped by the Announcer's queue.
 */
import { useEffect } from "react";
import { displayTravel, type Place } from "@/contracts/data";
import type { AppState, LayerFlags, Mood } from "@/contracts/store";
import { GROUPS } from "@/contracts/taxonomy";
import { compass } from "@/lib/geo";
import { announce, on } from "@/store/bus";
import { selectCategory, selectPlace, selectVisiblePlaces } from "@/store/selectors";
import { appStore, getApp } from "@/store/store";
import {
  bloomDoneText,
  DESELECT_TEXT,
  DRIVE_STOPPED_TEXT,
  driveArrivedText,
  driveStartText,
  groupText,
  INTRO_DONE_TEXT,
  layerText,
  LOAD_ERROR_TEXT,
  moodText,
  searchText,
  selectArrivedText,
} from "./announceText";

export const MOOD_LABELS: Record<Mood, string> = { day: "Day", golden: "Golden hour", night: "Night" };

/** The moments this hook can speak; pass some in `skip` when a module speaks them itself. */
export type AnnounceMoment = "group" | "bloom" | "search" | "select" | "deselect" | "mood" | "layers" | "drive" | "loadError" | "introDone";

export interface AnnouncementsOptions {
  /** Moments another module already announces (the Announcer drops same-moment duplicates anyway). */
  skip?: readonly AnnounceMoment[];
}
export const ANNOUNCE_TIMING = { groupMs: 250, searchMs: 600, selectFallbackMs: 900 } as const;

/** The §H.3 select text for a place in the current dataset. */
export function selectTextFor(s: AppState, p: Place): string {
  const cat = selectCategory(s, p.category);
  return selectArrivedText({
    name: p.name,
    status: p.status,
    crowKm: p.crowKm,
    compassWord: compass(p.bearing),
    travel: displayTravel(p),
    kind: cat?.kind,
    isSite: !!p.isSite,
  });
}

/** The §H.3 bloom text for a category in the current dataset. */
export function bloomTextFor(s: AppState, categoryId: string): string | null {
  const d = s.data;
  const cat = selectCategory(s, categoryId);
  if (!d || !cat) return null;
  let n = 0;
  let nearest: number | null = null;
  let allUpcoming = categoryId !== "future-infrastructure";
  for (const p of d.places) {
    if (p.category !== categoryId || p.isSite) continue;
    n++;
    if (p.status === "existing") allUpcoming = false;
    const t = displayTravel(p);
    if (t && (nearest === null || t.minutes < nearest)) nearest = t.minutes;
  }
  return bloomDoneText(categoryId, cat.label, n, nearest, allUpcoming && n > 0);
}

function groupLabel(s: AppState): string {
  return s.data?.groups.find((g) => g.id === s.group)?.label ?? GROUPS.find((g) => g.id === s.group)?.label ?? "Places";
}

let running = 0;

export function useAnnouncements(opts: AnnouncementsOptions = {}): void {
  const skipKey = (opts.skip ?? []).slice().sort().join(",");
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const skip = new Set(skipKey ? (skipKey.split(",") as AnnounceMoment[]) : []);
    const say = (m: AnnounceMoment, text: string, politeness: "polite" | "assertive" = "polite") => {
      if (!skip.has(m)) announce(text, politeness);
    };
    running++;
    if (running > 1) {
      return () => {
        running--;
      };
    }

    const timers = new Map<string, number>();
    const later = (key: string, ms: number, fn: () => void) => {
      window.clearTimeout(timers.get(key));
      timers.set(
        key,
        window.setTimeout(() => {
          timers.delete(key);
          fn();
        }, ms),
      );
    };
    const cancel = (key: string) => {
      window.clearTimeout(timers.get(key));
      timers.delete(key);
    };

    // Selection arrival bookkeeping.
    let pending: { id: string; drawing: boolean } | null = null;
    const speakSelection = (id: string) => {
      const s = getApp();
      if (s.selectedId !== id) return;
      const p = selectPlace(s, id);
      if (p) say("select", selectTextFor(s, p));
      pending = null;
    };

    const unsub = appStore.subscribe((s, p) => {
      if (s.phase === "attract") return;

      // selection
      const selectedNew = s.selectedId !== null && s.selectedId !== p.selectedId;
      if (s.selectedId !== p.selectedId) {
        cancel("select");
        if (selectedNew) cancel("group"); // a selection in another group moves the panel: the place speaks, not the group
        if (s.selectedId) {
          const id = s.selectedId;
          pending = { id, drawing: false };
          later("select", ANNOUNCE_TIMING.selectFallbackMs, () => {
            if (pending?.id === id && !pending.drawing) speakSelection(id);
          });
        } else {
          pending = null;
          if (p.selectedId && p.phase !== "attract") say("deselect", DESELECT_TEXT);
        }
      }

      // group (only when the user changed it: not a selection's or a chip's side effect)
      if (s.group !== p.group && !selectedNew && !s.category && !s.query.trim()) {
        later("group", ANNOUNCE_TIMING.groupMs, () => {
          const now = getApp();
          say("group", groupText(groupLabel(now), selectVisiblePlaces(now).length));
        });
      }

      // search
      if (s.query !== p.query) {
        if (s.query.trim()) {
          later("search", ANNOUNCE_TIMING.searchMs, () => {
            const now = getApp();
            if (now.query.trim()) say("search", searchText(selectVisiblePlaces(now).length));
          });
        } else cancel("search");
      }

      // mood (the intro's sunrise and kiosk loops are not the user's)
      if (s.mood !== p.mood && s.phase === "explore") say("mood", moodText(MOOD_LABELS[s.mood]));

      // layers: a single toggle (resetAll changes several at once and says nothing)
      if (s.layers !== p.layers) {
        const changed = (Object.keys(s.layers) as (keyof LayerFlags)[]).filter((k) => s.layers[k] !== p.layers[k]);
        if (changed.length === 1) say("layers", layerText(changed[0], s.layers[changed[0]]));
      }

      // drive start
      if (s.phase === "drive" && p.phase !== "drive") {
        const place = selectPlace(s, s.selectedId);
        if (place) say("drive", driveStartText(place.name));
      }

      // load error
      if (s.loadError && !p.loadError) say("loadError", LOAD_ERROR_TEXT, "assertive");
    });

    const offs = [
      on("route:start", ({ placeId }) => {
        if (pending?.id === placeId) pending.drawing = true;
      }),
      on("route:drawn", ({ placeId }) => {
        if (pending?.id === placeId) speakSelection(placeId);
      }),
      on("bloom:done", ({ categoryId }) => {
        const s = getApp();
        if (s.phase === "attract" || s.bloomCategory !== categoryId) return;
        const text = bloomTextFor(s, categoryId);
        if (text) say("bloom", text);
      }),
      on("drive:end", ({ placeId, reason }) => {
        const s = getApp();
        if (s.phase === "attract") return;
        const place = selectPlace(s, placeId);
        say("drive", reason === "done" && place ? driveArrivedText(place.name) : DRIVE_STOPPED_TEXT);
      }),
      on("intro:done", () => say("introDone", INTRO_DONE_TEXT)),
    ];

    return () => {
      running--;
      unsub();
      offs.forEach((off) => off());
      timers.forEach((t) => window.clearTimeout(t));
      timers.clear();
    };
  }, [skipKey]);
}
